import { Info } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useSwitchOrganization } from "@/lib/use-switch-organization";

export type CreateOrgOption = { id: string; label: string };

type Props = {
  /** Primeira opção deve ser sempre a organização Pessoal. */
  options: CreateOrgOption[];
  /** Chamado depois que a organização ativa já trocou — usado pra limpar campos do formulário
   *  que dependem de escopo de organização (dentista, serviço, laboratório). */
  onSwitched?: () => void;
};

/** Seletor de organização pros diálogos de criar Paciente/Consulta/Pedido, a partir da
 *  organização Pessoal — trocar a opção troca a organização ativa de verdade (reaproveita
 *  organization.switch), então o restante do diálogo passa a operar nessa organização como se o
 *  usuário tivesse trocado pela sidebar. Some se não há outra organização própria pra oferecer. */
export function CreateOrgPicker({ options, onSwitched }: Props) {
  const { user } = useAuth();
  const { switchTo, isSwitching } = useSwitchOrganization();
  const activeId = user?.activeOrganization?.id ?? "";

  if (options.length <= 1) return null;

  const selected = options.find((o) => o.id === activeId) ?? options[0];
  const isPersonalSelected = options[0]?.id === activeId;

  const handleChange = async (id: string) => {
    if (id === activeId) return;
    try {
      await switchTo(id);
      onSwitched?.();
    } catch (err: any) {
      toast.error(err?.message || "Erro ao trocar de organização.");
    }
  };

  return (
    <div className="space-y-2">
      <label className="block rounded-lg border border-zinc-200 bg-white px-3 py-2 transition-colors focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500 hover:border-zinc-300 dark:border-border dark:bg-card dark:hover:border-border">
        <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
          Cadastrar em
        </span>
        <select
          className="w-full cursor-pointer border-0 bg-transparent p-0 text-sm text-zinc-900 outline-none focus:outline-none focus:ring-0 dark:bg-card dark:text-foreground"
          value={activeId}
          disabled={isSwitching}
          onChange={(e) => handleChange(e.target.value)}
        >
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      {!isPersonalSelected && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p>
            Este cadastro será salvo em <strong>{selected.label}</strong> — você passará a
            trabalhar nessa organização ao salvar.
          </p>
        </div>
      )}
    </div>
  );
}
